import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Plus, ChevronUp, ChevronDown, Trash2, ArrowLeft } from 'lucide-react';
import { useEvent, useApiErrors, useLazyCommand } from '@soap/modules';
import { ThinkingAnimation } from '../../ThinkingAnimation';
import { Dialog, DialogContent } from '../../../ui/dialog';
import { tenantCustomCatalogSubjectRowsFromLibrary } from '../utils/curriculumUtils';
import {
  parseEducationSystemDefinitionsEvent,
  getDefinitionForKey,
  getYearsForKeyStageId,
  isPlacementIdsValidForDefinition,
  type EducationSystemDefinition
} from '../../../utils/educationSystemDefinitions';
import { SelectOrCreateDropdown } from './SelectOrCreateDropdown';
import {
  buildC314BandsAddingKeyStageOnly,
  buildC314BandsAddingYearOnly,
  buildC314BandsEnsuringPlacement,
  isTenantCustomEducationSystemKey,
  tenantCustomSystemIdFromKey
} from '../../../utils/customEducationSystemPlacement';
import { AppMessageDialog, PLACEMENT_APP_MSG_TITLE } from '../../AppMessageDialog';

interface CreateCustomTopicsDialogProps {
  showDialog: boolean;
  onClose: () => void;
  /** E308 mapped to library shape — subjects come from here. */
  tenantCustomCurriculumLibraryData?: any | null;
  /** Pre-selected subject from Create Your Own wizard (step 2 → batch topics). */
  initialCatalogSubjectId?: string;
  /** Hide subject dropdown when subject was chosen in the wizard. */
  hideSubjectPicker?: boolean;
  onGetTopicNames: (command: any) => void;
  onAddTopicsBatch: (command: any) => void;
  onLoadEducationSystemDefinitions?: () => void;
  onRefreshTenantCustomCurriculum?: () => void;
  /** Close this dialog and open Custom Subject (`C316v1_EnsureCustomSubjectInTenantCustomCurriculum`). */
  onOpenCustomSubjectShell?: () => void;
}

const TOPICS_PER_YEAR_MAX = 50;
const BATCH_SIZE = 5;

const viewStates = {
  closed: 0,
  form: 1,
  waitingForTopicNames: 2,
  editList: 3,
  creating: 4,
  cancelling: 5 // Cancelled but waiting for in-flight batch to complete
};

/** Tenant custom "extend placement" UI only: labels from create/select controls → E313 ids. */
function extendPlacementIdsFromBandLabels(
  def: EducationSystemDefinition | undefined,
  keyStageName: string,
  yearName: string
): { keyStageId: string; yearId: string } | null {
  if (!def?.keyStages?.length) return null;
  const wantKs = keyStageName.trim().toLowerCase();
  const wantY = yearName.trim().toLowerCase();
  if (!wantKs || !wantY) return null;
  const ks = def.keyStages.find((k) => k.name.trim().toLowerCase() === wantKs);
  const keyStageId = ks?.keyStageId?.trim();
  if (!ks || !keyStageId) return null;
  const hit = ks.years.find((y) => y.yearName.trim().toLowerCase() === wantY);
  if (!hit) return null;
  return { keyStageId, yearId: hit.yearId };
}

export function CreateCustomTopicsDialog({
  showDialog,
  onClose,
  tenantCustomCurriculumLibraryData,
  initialCatalogSubjectId,
  hideSubjectPicker = false,
  onGetTopicNames,
  onAddTopicsBatch,
  onLoadEducationSystemDefinitions,
  onRefreshTenantCustomCurriculum,
  onOpenCustomSubjectShell
}: CreateCustomTopicsDialogProps) {
  const catalogTitle = 'Custom curriculum (catalog)';
  const [sendSaveEducationStructure] = useLazyCommand('C314v1_SaveTenantEducationSystemStructure');

  const [viewState, setViewState] = useState(viewStates.closed);
  const [selectedCatalogSubjectId, setSelectedCatalogSubjectId] = useState('');
  const [placementDefs, setPlacementDefs] = useState<EducationSystemDefinition[]>([]);
  const [placementKeyStageName, setPlacementKeyStageName] = useState('');
  const [placementYearLabel, setPlacementYearLabel] = useState('');
  const [placementKeyStageId, setPlacementKeyStageId] = useState('');
  const [placementYearId, setPlacementYearId] = useState('');
  const [topicsPerYear, setTopicsPerYear] = useState<number | ''>('');
  const [minHours, setMinHours] = useState<number | ''>('');
  const [description, setDescription] = useState<string>('');
  const [placementStructureSaving, setPlacementStructureSaving] = useState(false);
  const [appMessage, setAppMessage] = useState<{ title: string; message: string } | null>(null);

  const showAppMessage = (message: string, title: string) => {
    setAppMessage({ title, message });
  };

  /** Avoid resetting to keyStages[0] when E313 is refreshed (e.g. after adding a key stage). */
  const lastPlacementEducationKeyRef = useRef<string | undefined>(undefined);

  const subjectRows = useMemo(
    () => tenantCustomCatalogSubjectRowsFromLibrary(tenantCustomCurriculumLibraryData),
    [tenantCustomCurriculumLibraryData]
  );

  const catalogReady = tenantCustomCurriculumLibraryData != null;
  const hasSubjects = hideSubjectPicker
    ? Boolean((initialCatalogSubjectId ?? selectedCatalogSubjectId).trim())
    : subjectRows.length > 0;

  const selectedSubjectRow = useMemo(
    () => subjectRows.find((r) => r.catalogSubjectId === selectedCatalogSubjectId),
    [subjectRows, selectedCatalogSubjectId]
  );

  const subjectName = selectedSubjectRow?.name.trim() ?? '';

  const resolvedEducationSystemKey = selectedSubjectRow?.educationSystemKey.trim() ?? '';

  const catalogSubjectIdForPicker = selectedSubjectRow ? selectedCatalogSubjectId.trim() : '';

  // Step 2: Topic list editing
  const [topicNames, setTopicNames] = useState<string[]>([]);
  const [topicGuidance, setTopicGuidance] = useState<string[]>([]); // Per-topic guidance matching topicNames array
  const [overallGuidance, setOverallGuidance] = useState<string>(''); // Overall guidance for all topics
  const [expandedGuidanceIndex, setExpandedGuidanceIndex] = useState<number | null>(null); // Which topic's guidance is expanded
  
  // Step 3: Batch creation
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [totalTopics, setTotalTopics] = useState(0);
  const [topicsCreated, setTopicsCreated] = useState(0);

  const placementIdsRef = useRef<{ keyStageId: string; yearId: string } | null>(null);

  // Use useApiErrors to handle errors
  useApiErrors(() => {
    setPlacementStructureSaving(false);
    if (viewState === viewStates.creating || viewState === viewStates.cancelling || viewState === viewStates.waitingForTopicNames) {
      setViewState(viewStates.form);
    }
  });

  useEffect(() => {
    if (!selectedCatalogSubjectId) return;
    if (!subjectRows.some((r) => r.catalogSubjectId === selectedCatalogSubjectId)) {
      setSelectedCatalogSubjectId('');
    }
  }, [subjectRows, selectedCatalogSubjectId]);

  // Listen for E200 - topic names received
  useEvent({
    eventName: 'E200v1_GotCustomSubjectTopicNames',
    onEventReceived: (event: any) => {
      if (viewState === viewStates.waitingForTopicNames) {
        // Parse pipe-delimited string to array
        const topicNamesString = event.e200_TopicNames || '';
        const parsedNames = topicNamesString
          .split('|')
          .map((name: string) => name.trim())
          .filter((name: string) => name.length > 0);
        setTopicNames(parsedNames);
        // Initialize guidance arrays to match topic names
        setTopicGuidance(new Array(parsedNames.length).fill(''));
        // Pre-populate overall guidance from backend relevancy analysis
        // Backend analyzes if description themes align with topic names and returns relevant guidance
        // If analysis found relevant themes, use them; otherwise fall back to original description
        // Property may be E200_Description (C#) or e200_Description (JS) - check both safely
        const relevantGuidance = event?.e200_Description || event?.E200_Description || null;
        if (relevantGuidance && typeof relevantGuidance === 'string' && relevantGuidance.trim()) {
          // Backend found relevant themes - use them
          setOverallGuidance(relevantGuidance.trim());
        } else if (description.trim()) {
          // Backend analysis didn't find relevant themes or failed - use original description as fallback
          // User can still edit it if needed
          setOverallGuidance(description.trim());
        }
        setViewState(viewStates.editList);
      }
    }
  });

  useEvent({
    eventName: 'E313v1_GotEducationSystemDefinitions',
    onEventReceived: (event: any) => {
      setPlacementDefs(parseEducationSystemDefinitionsEvent(event));
    }
  });

  useEvent({
    eventName: 'E201v1_CustomSubjectTopicsBatchAdded',
    onEventReceived: (event: any) => {
      // Process event if we're creating or cancelling (waiting for in-flight batch)
      if (viewState === viewStates.creating || viewState === viewStates.cancelling) {
        setTopicsCreated(event.e201_TotalTopicsAdded ? Number(event.e201_TotalTopicsAdded) : 0);
        
        // If cancelling, close after this batch completes
        if (viewState === viewStates.cancelling) {
          setViewState(viewStates.closed);
          onClose();
          // Trigger refresh to show what was created before cancellation
          if (onRefreshTenantCustomCurriculum) {
            onRefreshTenantCustomCurriculum();
          } else {
            window.location.reload();
          }
          return;
        }
        
        // Check if we have more batches to send
        const batches = chunkArray(topicNames, BATCH_SIZE);
        const nextBatchIndex = currentBatchIndex + 1;
        
        if (nextBatchIndex < batches.length) {
          // Send next batch
          setCurrentBatchIndex(nextBatchIndex);
          sendBatch(batches[nextBatchIndex], nextBatchIndex);
        } else {
          // All batches complete
          setViewState(viewStates.closed);
          onClose();
          // Trigger refresh of student curriculum
          if (onRefreshTenantCustomCurriculum) {
            onRefreshTenantCustomCurriculum();
          } else {
            // Fallback to page reload if refresh callback not provided
            window.location.reload();
          }
        }
      }
    }
  });

  // Load once when the dialog opens. Do not list onLoadEducationSystemDefinitions in deps — the parent often passes
  // an inline handler that changes every render, which would re-fire this effect and spam C313.
  useEffect(() => {
    if (!showDialog) return;
    setSelectedCatalogSubjectId((initialCatalogSubjectId ?? '').trim());
    onRefreshTenantCustomCurriculum?.();
    onLoadEducationSystemDefinitions?.();
  }, [showDialog, initialCatalogSubjectId]);

  useEffect(() => {
    if (showDialog) {
      setViewState(viewStates.form);
    }
  }, [showDialog]);

  const currentPlacement = useMemo(
    () => getDefinitionForKey(placementDefs, resolvedEducationSystemKey),
    [placementDefs, resolvedEducationSystemKey]
  );

  const allowExtendPlacement = useMemo(
    () =>
      Boolean(resolvedEducationSystemKey) && isTenantCustomEducationSystemKey(resolvedEducationSystemKey),
    [resolvedEducationSystemKey]
  );

  const yearValue = placementYearLabel.trim();
  const placementFromE313 = useMemo(() => {
    if (allowExtendPlacement) {
      return extendPlacementIdsFromBandLabels(currentPlacement, placementKeyStageName, yearValue);
    }
    const ks = placementKeyStageId.trim();
    const y = placementYearId.trim();
    if (!ks || !y || !currentPlacement) return null;
    return isPlacementIdsValidForDefinition(currentPlacement, ks, y)
      ? { keyStageId: ks, yearId: y }
      : null;
  }, [
    allowExtendPlacement,
    currentPlacement,
    placementKeyStageName,
    yearValue,
    placementKeyStageId,
    placementYearId
  ]);

  useEffect(() => {
    placementIdsRef.current = placementFromE313;
  }, [placementFromE313]);

  useEffect(() => {
    if (resolvedEducationSystemKey === lastPlacementEducationKeyRef.current) {
      return;
    }
    lastPlacementEducationKeyRef.current = resolvedEducationSystemKey;
    setPlacementKeyStageName('');
    setPlacementYearLabel('');
    setPlacementKeyStageId('');
    setPlacementYearId('');
  }, [resolvedEducationSystemKey]);

  useEffect(() => {
    if (!resolvedEducationSystemKey || placementKeyStageName) return;
    if (!allowExtendPlacement) return;
    const def = getDefinitionForKey(placementDefs, resolvedEducationSystemKey);
    const firstKs = def?.keyStages?.[0];
    if (!firstKs?.name) {
      setPlacementKeyStageName('');
      setPlacementYearLabel('');
      return;
    }
    setPlacementKeyStageName(firstKs.name);
    setPlacementYearLabel(firstKs.years?.[0]?.yearName ?? '');
  }, [resolvedEducationSystemKey, placementDefs, placementKeyStageName, allowExtendPlacement]);

  useEffect(() => {
    if (allowExtendPlacement || !resolvedEducationSystemKey) return;
    const def = getDefinitionForKey(placementDefs, resolvedEducationSystemKey);
    const withIds = (def?.keyStages ?? []).filter((k) => (k.keyStageId ?? '').trim() !== '');
    const first = withIds[0];
    const y0 = first?.years?.[0]?.yearId ?? '';
    setPlacementKeyStageId(first?.keyStageId ?? '');
    setPlacementYearId(y0);
  }, [allowExtendPlacement, resolvedEducationSystemKey, placementDefs]);

  const handleKeyStagePlacementChange = (name: string): void | Promise<void> => {
    const trimmed = name.trim();
    if (!allowExtendPlacement) {
      setPlacementKeyStageName(trimmed);
      return;
    }
    if (!trimmed) return;

    const def = getDefinitionForKey(placementDefs, resolvedEducationSystemKey);
    if (!def) {
      showAppMessage(
        'Placement is still loading. Wait a moment and try again.',
        PLACEMENT_APP_MSG_TITLE.notReady
      );
      throw new Error('Placement not ready');
    }
    const exists = def.keyStages.some((k) => k.name.trim().toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setPlacementYearLabel('');
      setPlacementKeyStageName(trimmed);
      return;
    }

    return (async () => {
      const systemId = tenantCustomSystemIdFromKey(resolvedEducationSystemKey);
      if (!systemId) {
        showAppMessage('Invalid custom education system key.', PLACEMENT_APP_MSG_TITLE.invalidSystem);
        throw new Error('Invalid system key');
      }
      const built = buildC314BandsAddingKeyStageOnly(def, trimmed, () => crypto.randomUUID());
      if (!built.ok) {
        showAppMessage(built.message, PLACEMENT_APP_MSG_TITLE.cannotSave);
        throw new Error(built.message);
      }
      if (!built.needsSave) {
        setPlacementYearLabel('');
        setPlacementKeyStageName(trimmed);
        return;
      }
      setPlacementStructureSaving(true);
      try {
        await sendSaveEducationStructure({
          C314_SystemId: systemId,
          C314_KeyStageBands: built.bands
        });
        setPlacementYearLabel('');
        setPlacementKeyStageName(trimmed);
        onLoadEducationSystemDefinitions?.();
      } catch (error) {
        console.error(error);
        showAppMessage('Could not save education system structure.', PLACEMENT_APP_MSG_TITLE.couldNotSave);
        throw error instanceof Error ? error : new Error('Save failed');
      } finally {
        setPlacementStructureSaving(false);
      }
    })();
  };

  const handleYearPlacementChange = (label: string): void | Promise<void> => {
    const yTrim = label.trim();
    if (!allowExtendPlacement) {
      setPlacementYearLabel(yTrim);
      return;
    }
    if (!placementKeyStageName.trim()) return;

    const def = getDefinitionForKey(placementDefs, resolvedEducationSystemKey);
    if (!def) {
      showAppMessage(
        'Placement is still loading. Wait a moment and try again.',
        PLACEMENT_APP_MSG_TITLE.notReady
      );
      throw new Error('Placement not ready');
    }
    const ks = def.keyStages.find(
      (k) => k.name.trim().toLowerCase() === placementKeyStageName.trim().toLowerCase()
    );
    const existing = (ks?.years ?? []).some((yl) => yl.yearName.toLowerCase() === yTrim.toLowerCase());
    if (existing || !yTrim) {
      setPlacementYearLabel(yTrim);
      return;
    }

    return (async () => {
      const systemId = tenantCustomSystemIdFromKey(resolvedEducationSystemKey);
      if (!systemId) {
        showAppMessage('Invalid custom education system key.', PLACEMENT_APP_MSG_TITLE.invalidSystem);
        throw new Error('Invalid system key');
      }
      const built = buildC314BandsAddingYearOnly(def, placementKeyStageName, yTrim, () => crypto.randomUUID());
      if (!built.ok) {
        showAppMessage(built.message, PLACEMENT_APP_MSG_TITLE.cannotSave);
        throw new Error(built.message);
      }
      if (!built.needsSave) {
        setPlacementYearLabel(yTrim);
        return;
      }
      setPlacementStructureSaving(true);
      try {
        await sendSaveEducationStructure({
          C314_SystemId: systemId,
          C314_KeyStageBands: built.bands
        });
        setPlacementYearLabel(yTrim);
        onLoadEducationSystemDefinitions?.();
      } catch (error) {
        console.error(error);
        showAppMessage('Could not save education system structure.', PLACEMENT_APP_MSG_TITLE.couldNotSave);
        throw error instanceof Error ? error : new Error('Save failed');
      } finally {
        setPlacementStructureSaving(false);
      }
    })();
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!showDialog) {
      setAppMessage(null);
      lastPlacementEducationKeyRef.current = undefined;
      setSelectedCatalogSubjectId('');
      setTopicsPerYear('');
      setMinHours('');
      setDescription('');
      setPlacementStructureSaving(false);
      setPlacementDefs([]);
      setPlacementKeyStageName('');
      setPlacementYearLabel('');
      setPlacementKeyStageId('');
      setPlacementYearId('');
      setTopicNames([]);
      setTopicGuidance([]);
      setOverallGuidance('');
      setExpandedGuidanceIndex(null);
      setViewState(viewStates.closed);
      setCurrentBatchIndex(0);
      setTotalTopics(0);
      setTopicsCreated(0);
      placementIdsRef.current = null;
    }
  }, [showDialog]);

  const handleGenerateTopicList = async () => {
    if (!subjectName.trim() || !resolvedEducationSystemKey || topicsPerYear === '') {
      return;
    }
    if (allowExtendPlacement && !yearValue.trim()) return;
    if (!allowExtendPlacement && (!placementKeyStageId.trim() || !placementYearId.trim())) return;

    const topics = Number(topicsPerYear);
    if (topics < 1 || topics > TOPICS_PER_YEAR_MAX) {
      return;
    }

    setViewState(viewStates.waitingForTopicNames);

    await new Promise(resolve => requestAnimationFrame(resolve));

    try {
      let ids = placementFromE313;
      if (allowExtendPlacement && !ids && currentPlacement) {
        const systemId = tenantCustomSystemIdFromKey(resolvedEducationSystemKey);
        if (!systemId) {
          showAppMessage('Invalid custom education system key.', PLACEMENT_APP_MSG_TITLE.invalidSystem);
          setViewState(viewStates.form);
          return;
        }
        const built = buildC314BandsEnsuringPlacement(
          currentPlacement,
          placementKeyStageName,
          placementYearLabel,
          () => crypto.randomUUID()
        );
        if (!built.ok) {
          showAppMessage(built.message, PLACEMENT_APP_MSG_TITLE.cannotSave);
          setViewState(viewStates.form);
          return;
        }
        if (built.needsSave) {
          await sendSaveEducationStructure({
            C314_SystemId: systemId,
            C314_KeyStageBands: built.bands
          });
          onLoadEducationSystemDefinitions?.();
        }
        ids = { keyStageId: built.keyStageId, yearId: built.yearId };
      }
      if (!ids && !allowExtendPlacement) {
        showAppMessage(
          'Could not resolve key stage and year. Refresh the page or check Control Panel → Education system.',
          PLACEMENT_APP_MSG_TITLE.notReady
        );
        setViewState(viewStates.form);
        return;
      }
      if (!ids?.keyStageId?.trim() || !ids?.yearId?.trim()) {
        showAppMessage(
          'Could not resolve key stage and year. Refresh the page or check Control Panel → Education system.',
          PLACEMENT_APP_MSG_TITLE.notReady
        );
        setViewState(viewStates.form);
        return;
      }
      placementIdsRef.current = ids;

      const catalogSid = catalogSubjectIdForPicker.trim();
      if (!catalogSid) {
        showAppMessage(
          'Could not resolve catalog subject id for this subject and education system.',
          PLACEMENT_APP_MSG_TITLE.notReady
        );
        setViewState(viewStates.form);
        return;
      }

      const command = {
        c297_TopicsPerYear: Number(topicsPerYear),
        c297_Description: description.trim() || undefined,
        c297_EducationSystemKey: resolvedEducationSystemKey,
        c297_CatalogSubjectId: catalogSid,
        c297_EducationSystemKeyStageId: ids.keyStageId,
        c297_EducationSystemYearId: ids.yearId
      };

      onGetTopicNames(command);
    } catch (error) {
      console.error('Error getting topic names:', error);
      setViewState(viewStates.form);
    }
  };

  const handleContinueFromList = () => {
    if (topicNames.length === 0) {
      return;
    }

    // Start batch creation
    setViewState(viewStates.creating);
    setTotalTopics(topicNames.length);
    setTopicsCreated(0);
    setCurrentBatchIndex(0);

    // Send first batch
    const batches = chunkArray(topicNames, BATCH_SIZE);
    sendBatch(batches[0], 0);
  };

  const sendBatch = (batchTopicNames: string[], batchIndex: number) => {
    // Don't send if we're cancelling or closed
    if (viewState === viewStates.cancelling || viewState === viewStates.closed) return;

    // Convert array to pipe-delimited string
    const topicNamesPipeDelimited = batchTopicNames.join('|');

    // Get guidance for topics in this batch
    const batches = chunkArray(topicNames, BATCH_SIZE);
    let batchStartIndex = 0;
    for (let i = 0; i < batchIndex; i++) {
      batchStartIndex += batches[i].length;
    }
    const batchGuidance: string[] = [];
    for (let i = 0; i < batchTopicNames.length; i++) {
      const topicIndex = batchStartIndex + i;
      batchGuidance.push(topicGuidance[topicIndex] || '');
    }
    const batchGuidancePipeDelimited = batchGuidance.join('|');

    const catalogSid = catalogSubjectIdForPicker.trim();
    if (!catalogSid) return;

    const ids = placementIdsRef.current ?? placementFromE313;
    if (!ids?.keyStageId?.trim() || !ids?.yearId?.trim()) return;

    const command = {
      c298_SubjectName: subjectName.trim(),
      c298_TopicNames: topicNamesPipeDelimited,
      c298_TopicGuidance: batchGuidancePipeDelimited || undefined,
      c298_OverallGuidance: overallGuidance.trim() || undefined,
      c298_MinHours: minHours !== '' ? Number(minHours) : undefined,
      c298_Description: description.trim() || undefined,
      c298_EducationSystemKey: resolvedEducationSystemKey,
      c298_CatalogSubjectId: catalogSid,
      c298_EducationSystemKeyStageId: ids.keyStageId,
      c298_EducationSystemYearId: ids.yearId
    };

    onAddTopicsBatch(command);
  };

  const handleCancel = () => {
    // If we're creating (batch in-flight), switch to cancelling state
    // The E201 event handler will close the dialog after the current batch finishes
    if (viewState === viewStates.creating) {
      setViewState(viewStates.cancelling);
      return;
    }
    
    // No batch in-flight, close immediately
    setViewState(viewStates.closed);
    onClose();
    // Trigger refresh to show what was created
    if (onRefreshTenantCustomCurriculum) {
      onRefreshTenantCustomCurriculum();
    } else {
      // Fallback to page reload if refresh callback not provided
      window.location.reload();
    }
  };

  const handleEditTopicName = (index: number, newName: string) => {
    const updated = [...topicNames];
    updated[index] = newName;
    setTopicNames(updated);
    // Ensure guidance array matches topic names length
    const updatedGuidance = [...topicGuidance];
    if (updatedGuidance.length <= index) {
      updatedGuidance.length = index + 1;
      updatedGuidance[index] = updatedGuidance[index] || '';
    }
    setTopicGuidance(updatedGuidance);
  };

  const handleEditTopicGuidance = (index: number, guidance: string) => {
    const updated = [...topicGuidance];
    if (updated.length <= index) {
      updated.length = index + 1;
    }
    updated[index] = guidance;
    setTopicGuidance(updated);
  };

  const handleRemoveTopic = (index: number) => {
    const updated = topicNames.filter((_, i) => i !== index);
    setTopicNames(updated);
    const updatedGuidance = topicGuidance.filter((_, i) => i !== index);
    setTopicGuidance(updatedGuidance);
    if (expandedGuidanceIndex === index) {
      setExpandedGuidanceIndex(null);
    } else if (expandedGuidanceIndex !== null && expandedGuidanceIndex > index) {
      setExpandedGuidanceIndex(expandedGuidanceIndex - 1);
    }
  };

  const handleAddTopic = () => {
    setTopicNames([...topicNames, '']);
    setTopicGuidance([...topicGuidance, '']);
  };

  const handleMoveTopic = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === topicNames.length - 1) return;

    const updated = [...topicNames];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setTopicNames(updated);
  };

  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  const existingKeyStageNames = useMemo(
    () =>
      (currentPlacement?.keyStages ?? [])
        .map((ks) => ks.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [currentPlacement]
  );

  const existingYearLabelsForKeyStage = useMemo(() => {
    const ks = (currentPlacement?.keyStages ?? []).find(
      (k) => k.name.trim().toLowerCase() === placementKeyStageName.trim().toLowerCase()
    );
    return (ks?.years ?? [])
      .map((y) => y.yearName)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [currentPlacement, placementKeyStageName]);

  const placementReady =
    Boolean(currentPlacement) && (allowExtendPlacement || Boolean(currentPlacement?.keyStages?.length));
  const yearValid = allowExtendPlacement ? yearValue.length > 0 : placementYearId.trim().length > 0;
  const topicsNum = topicsPerYear === '' ? NaN : Number(topicsPerYear);
  const topicsInRange = !Number.isNaN(topicsNum) && topicsNum >= 1 && topicsNum <= TOPICS_PER_YEAR_MAX;

  const isFormValid =
    catalogReady &&
    hasSubjects &&
    !placementStructureSaving &&
    Boolean(selectedCatalogSubjectId.trim()) &&
    subjectName.trim() &&
    Boolean(resolvedEducationSystemKey) &&
    Boolean(catalogSubjectIdForPicker?.trim()) &&
    placementReady &&
    (allowExtendPlacement ? placementKeyStageName.trim().length > 0 : placementKeyStageId.trim().length > 0) &&
    yearValid &&
    topicsPerYear !== '' &&
    topicsInRange;

  const isListValid = topicNames.length > 0 && topicNames.every(name => name.trim().length > 0);

  if (!showDialog) return null;

  const appMessageDialog = (
    <AppMessageDialog
      open={appMessage != null}
      onOpenChange={(open) => {
        if (!open) setAppMessage(null);
      }}
      title={appMessage?.title ?? ''}
      message={appMessage?.message ?? ''}
    />
  );

  // Step 3: Creating or Cancelling (batch progress)
  if (viewState === viewStates.creating || viewState === viewStates.cancelling) {
    const batches = chunkArray(topicNames, BATCH_SIZE);
    const canCancel = viewState === viewStates.creating && (currentBatchIndex < batches.length - 1 || currentBatchIndex === batches.length - 1);
    const isCancelling = viewState === viewStates.cancelling;
    
    // Calculate the current batch range for display
    let currentBatchStart = 1;
    let currentBatchEnd = totalTopics;
    if (batches.length > 0 && currentBatchIndex < batches.length) {
      // Calculate start: sum of all previous batches + 1
      for (let i = 0; i < currentBatchIndex; i++) {
        currentBatchStart += batches[i].length;
      }
      // Calculate end: start + current batch size - 1
      currentBatchEnd = currentBatchStart + batches[currentBatchIndex].length - 1;
    }
    
    // Display text: show range if batch has multiple topics, otherwise show single topic number
    const displayText = currentBatchStart === currentBatchEnd
      ? `Creating topic ${currentBatchStart} of ${totalTopics}`
      : `Creating topics ${currentBatchStart}-${currentBatchEnd} of ${totalTopics}`;

    const showProgressBar = batches.length > 1;

    return (
      <>
        {appMessageDialog}
        <Dialog open onOpenChange={() => {}}>
          <DialogContent dismissible={false} className="max-w-2xl sm:max-w-2xl">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {isCancelling ? 'Cancelling...' : 'Creating Topics'}
            </h2>
            {showProgressBar && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>{displayText}</span>
                  <span>{Math.round((topicsCreated / totalTopics) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-action-blue h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, Math.max(0, (topicsCreated / totalTopics) * 100))}%` }}
                  />
                </div>
              </div>
            )}
            <ThinkingAnimation message={isCancelling ? `Finishing current batch, then cancelling...` : `${displayText}...`} />
            {canCancel && !isCancelling && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            )}
            {isCancelling && (
              <div className="mt-4 text-sm text-gray-600 text-center">
                Waiting for current batch to complete...
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Step 2: Edit topic list
  if (viewState === viewStates.editList) {
    return (
      <>
        {appMessageDialog}
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Review and Edit Topic Names</h2>
            <button
              onClick={() => {
                setViewState(viewStates.form);
                setTopicNames([]);
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1">
            <div className="space-y-3">
              {topicNames.map((name, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col gap-1 flex-1">
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => handleEditTopicName(index, e.target.value)}
                        placeholder={`Topic ${index + 1}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue"
                      />
                    </div>
                    <button
                      onClick={() => handleMoveTopic(index, 'up')}
                      disabled={index === 0}
                      className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ChevronUp size={20} />
                    </button>
                    <button
                      onClick={() => handleMoveTopic(index, 'down')}
                      disabled={index === topicNames.length - 1}
                      className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ChevronDown size={20} />
                    </button>
                    <button
                      onClick={() => handleRemoveTopic(index)}
                      className="p-2 text-destructive-action/80 hover:text-destructive-action"
                      title="Remove"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                  <div className="pl-0">
                    <button
                      onClick={() => setExpandedGuidanceIndex(expandedGuidanceIndex === index ? null : index)}
                      className="text-sm text-action-blue hover:text-action-blue-dark transition-colors"
                    >
                      Add Additional Guidance {expandedGuidanceIndex === index ? '▼' : '▶'}
                    </button>
                    {expandedGuidanceIndex === index && (
                      <textarea
                        value={topicGuidance[index] || ''}
                        onChange={(e) => handleEditTopicGuidance(index, e.target.value)}
                        placeholder="Add specific guidance for this topic to help the AI create better content..."
                        rows={4}
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue resize-none"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overall Guidance <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <textarea
                value={overallGuidance}
                onChange={(e) => setOverallGuidance(e.target.value)}
                placeholder="Add overall guidance that applies to all topics. This will be combined with the description from step 1 to guide the AI when creating topic content..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue resize-none"
              />
              <p className="mt-1 text-xs text-gray-500">
                This guidance will be used for all topics in this batch. Use it to provide general instructions, themes, or requirements that apply across all topics.
              </p>
            </div>
            <button
              onClick={handleAddTopic}
              className="mt-4 px-4 py-2 text-action-blue border border-action-blue rounded-lg hover:bg-action-blue/10 transition-colors text-sm font-medium flex items-center gap-2"
            >
              <Plus size={16} />
              Add Topic
            </button>
          </div>

          <div className="p-6 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => setViewState(viewStates.form)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <button
              onClick={handleContinueFromList}
              disabled={!isListValid}
              className="px-4 py-2 bg-action-blue text-white rounded-lg hover:bg-action-blue-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
      </>
    );
  }

  // Step 1: Form
  const handleClose = () => {
    if (viewState === viewStates.creating || viewState === viewStates.cancelling) return;
    setViewState(viewStates.closed);
    onClose();
  };

  return (
    <>
      {appMessageDialog}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Custom topics — {catalogTitle}</h2>
          <button
            onClick={handleClose}
            disabled={viewState === viewStates.waitingForTopicNames}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {!catalogReady && <p className="text-sm text-gray-600 mb-4">Loading catalog…</p>}
          {catalogReady && !hasSubjects && !hideSubjectPicker && (
            <div className="space-y-4 mb-4">
              <div className="rounded-lg border border-action-blue/20 bg-action-blue/10 p-4 space-y-2">
                <p className="text-sm font-medium text-gray-900">
                  You do not have any custom subjects yet.
                </p>
                <p className="text-sm text-gray-700">
                  Add a custom subject first, then return here to create topics.
                </p>
              </div>
              <button
                type="button"
                onClick={() => onOpenCustomSubjectShell?.()}
                className="text-sm font-medium text-action-blue underline hover:text-action-blue-dark"
              >
                Add new custom subject
              </button>
            </div>
          )}
          <div
            className={`space-y-4 ${!catalogReady || !hasSubjects || placementStructureSaving ? 'pointer-events-none opacity-50' : ''}`}
          >
            {hideSubjectPicker ? (
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Subject</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{subjectName || 'Loading…'}</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-destructive-action">*</span>
                </label>
                <select
                  value={selectedCatalogSubjectId}
                  onChange={(e) => setSelectedCatalogSubjectId(e.target.value)}
                  disabled={!catalogReady || subjectRows.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue disabled:bg-gray-100"
                >
                  <option value="">{subjectRows.length === 0 ? 'No subjects available' : 'Select a subject'}</option>
                  {subjectRows.map((r) => (
                    <option key={r.catalogSubjectId} value={r.catalogSubjectId}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-sm text-gray-600">
                  Only subjects you created appear here.{' '}
                  <button
                    type="button"
                    onClick={() => onOpenCustomSubjectShell?.()}
                    className="font-medium text-action-blue underline hover:text-action-blue-dark"
                  >
                    Add new custom subject
                  </button>
                </p>
              </div>
            )}

            {/* Key stage and year from education system definition */}
            <div>
              {allowExtendPlacement ? (
                <SelectOrCreateDropdown
                  label="Key stage"
                  value={placementKeyStageName}
                  onChange={handleKeyStagePlacementChange}
                  existingOptions={existingKeyStageNames}
                  allowCreate
                  placeholder={
                    !selectedCatalogSubjectId
                      ? 'Select a subject first'
                      : !resolvedEducationSystemKey
                        ? 'Loading placement…'
                        : placementReady
                          ? 'Select or add key stage'
                          : 'Loading…'
                  }
                  disabled={!resolvedEducationSystemKey || !placementReady || placementStructureSaving}
                  required
                />
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Key stage <span className="text-destructive-action">*</span>
                  </label>
                  <select
                    value={placementKeyStageId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setPlacementKeyStageId(id);
                      const ks = (currentPlacement?.keyStages ?? []).find((k) => k.keyStageId === id);
                      if (ks) {
                        setPlacementKeyStageName(ks.name);
                        const y0 = ks.years[0];
                        setPlacementYearId(y0?.yearId ?? '');
                        setPlacementYearLabel(y0?.yearName ?? '');
                      }
                    }}
                    disabled={!resolvedEducationSystemKey || !placementReady}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue disabled:bg-gray-100"
                  >
                    <option value="">
                      {!selectedCatalogSubjectId
                        ? 'Select a subject first'
                        : !resolvedEducationSystemKey
                          ? 'Loading placement…'
                          : placementReady
                            ? 'Select key stage'
                            : 'Loading…'}
                    </option>
                    {(currentPlacement?.keyStages ?? [])
                      .filter((ks) => ks.keyStageId)
                      .map((ks) => (
                        <option key={ks.keyStageId!} value={ks.keyStageId!}>
                          {ks.name}
                        </option>
                      ))}
                  </select>
                </>
              )}
            </div>
            <div>
              {allowExtendPlacement ? (
                <SelectOrCreateDropdown
                  label="Year"
                  value={placementYearLabel}
                  onChange={handleYearPlacementChange}
                  existingOptions={existingYearLabelsForKeyStage}
                  allowCreate
                  placeholder={
                    !resolvedEducationSystemKey
                      ? 'Select a subject first'
                      : placementKeyStageName
                        ? 'Select or add year'
                        : 'Select key stage first'
                  }
                  disabled={!placementKeyStageName || !resolvedEducationSystemKey || placementStructureSaving}
                  required
                />
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year <span className="text-destructive-action">*</span>
                  </label>
                  <select
                    value={placementYearId}
                    onChange={(e) => {
                      const yid = e.target.value;
                      setPlacementYearId(yid);
                      const band = (currentPlacement?.keyStages ?? []).find(
                        (k) => k.keyStageId === placementKeyStageId
                      );
                      const y = band?.years.find((x) => x.yearId === yid);
                      if (y) setPlacementYearLabel(y.yearName);
                    }}
                    disabled={!placementKeyStageId || !resolvedEducationSystemKey}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue disabled:bg-gray-100"
                  >
                    <option value="">
                      {!resolvedEducationSystemKey
                        ? 'Select a subject first'
                        : placementKeyStageId
                          ? 'Select year'
                          : 'Select key stage first'}
                    </option>
                    {getYearsForKeyStageId(currentPlacement, placementKeyStageId).map((y) => (
                      <option key={y.yearId} value={y.yearId}>
                        {y.yearName}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {resolvedEducationSystemKey && !placementReady && (
                <p className="mt-1 text-sm text-amber-700">
                  Loading placement rules… If you use a custom education system, configure key stages and years in
                  Control Panel → Education System.
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">Years are defined by the selected education system.</p>
            </div>

            {/* Number of Topics */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Topics <span className="text-destructive-action">*</span>
              </label>
              <input
                type="number"
                value={topicsPerYear}
                onChange={(e) => setTopicsPerYear(e.target.value ? Number(e.target.value) : '')}
                placeholder="e.g., 5"
                min={1}
                max={TOPICS_PER_YEAR_MAX}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue"
              />
              {topicsPerYear !== '' && !topicsInRange && (
                <p className="mt-1 text-sm text-destructive-action">Number of topics must be between 1 and {TOPICS_PER_YEAR_MAX}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                The number of topics to generate for this year
              </p>
            </div>

            {/* Minimum Hours (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Hours (Per Topic) <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="number"
                value={minHours}
                onChange={(e) => setMinHours(e.target.value ? Number(e.target.value) : '')}
                placeholder="Enter minimum hours"
                min="0"
                step="0.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue"
              />
              <p className="mt-1 text-xs text-gray-500">
                Default minimum hours for each topic. Can be overridden per topic by the AI.
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the subject you want to create and any topics or areas of interest you want it to cover. You can be as detailed or brief as you like - the AI will use this information to generate appropriate topics for each year."
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue resize-none"
              />
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={viewState === viewStates.waitingForTopicNames}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerateTopicList}
            disabled={!isFormValid || viewState === viewStates.waitingForTopicNames}
            className="px-4 py-2 bg-action-blue text-white rounded-lg hover:bg-action-blue-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center gap-2"
          >
            {viewState === viewStates.waitingForTopicNames ? (
              <>
                <span className="animate-spin">⏳</span>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Plus size={16} />
                <span>Generate Topic List</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
